/**
 * src/features/dashboard/ui/ImportPreviewModal.tsx
 *
 * SAFE-03 — confirm-before-mutate preview for a price-list import.
 *
 * Shows the three counts a previewMergedServices() call already computed — created,
 * updated, blank-price-kept — before applyMergedServices() ever runs. The stats prop
 * is nullable and, when null, the component renders nothing: the modal only exists
 * once a merge has actually been computed to show.
 *
 * Reuses the project's own responsive modal (dialog on desktop, drawer on mobile —
 * see src/shared/ui/responsive-modal.tsx) rather than hand-rolling a second one, and
 * copies the chip/badge shape verbatim from src/features/referral/ui/StatusList.tsx,
 * including that component's inherited (off-scale) chip padding — not "corrected" here.
 *
 * No destructive treatment: importing a price list is reversible, and a blank price
 * cell explicitly keeps the service's current price rather than losing it.
 */
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveModal } from '@/shared/ui/responsive-modal';
import Button from '@/shared/ui/button';
import type { ImportStats } from '@/entities/service';

export interface ImportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  stats: ImportStats | null;
}

export const ImportPreviewModal: FC<ImportPreviewModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  stats,
}) => {
  const { t } = useTranslation();

  if (!stats) return null;

  const isEmpty = stats.created === 0 && stats.updated === 0 && stats.blankKept === 0;

  return (
    <ResponsiveModal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={t('dashboard.import_preview.title', 'Import preview')}
      className="max-w-[360px]"
    >
      {isEmpty ? (
        <div className="py-4 text-center space-y-1" data-testid="import-preview-empty">
          <p className="text-body font-bold text-slate-700 dark:text-slate-300">
            {t('dashboard.import_preview.no_changes', 'No changes found in this file — nothing to import.')}
          </p>
          <div className="mt-5">
            <Button
              variant="ghost"
              className="w-full h-12 text-sm font-bold text-slate-400"
              onClick={onClose}
              data-testid="import-preview-close-btn"
            >
              {t('common.close', 'Close')}
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="flex flex-col gap-2">
            <div
              className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700"
              data-testid="import-preview-row-created"
            >
              <span
                className="text-micro font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                role="status"
              >
                {stats.created}
              </span>
              <span className="text-body text-slate-600 dark:text-slate-400">
                {t('dashboard.import_preview.created', 'New services: {{count}}', {
                  count: stats.created,
                })}
              </span>
            </div>

            <div
              className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700"
              data-testid="import-preview-row-updated"
            >
              <span
                className="text-micro font-bold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                role="status"
              >
                {stats.updated}
              </span>
              <span className="text-body text-slate-600 dark:text-slate-400">
                {t('dashboard.import_preview.updated', 'Prices to update: {{count}}', {
                  count: stats.updated,
                })}
              </span>
            </div>

            <div
              className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-100 dark:border-slate-700"
              data-testid="import-preview-row-blank-kept"
            >
              <span
                className="text-micro font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                role="status"
              >
                {stats.blankKept}
              </span>
              <span className="text-body text-slate-600 dark:text-slate-400">
                {t('dashboard.import_preview.blank_kept', 'Blank price — current price kept: {{count}}', {
                  count: stats.blankKept,
                })}
              </span>
            </div>
          </div>

          <div className="flex flex-col w-full gap-2 mt-6">
            <Button
              variant="default"
              className="w-full h-12 text-sm font-bold rounded-xl"
              onClick={onConfirm}
              data-testid="import-preview-confirm-btn"
            >
              {t('dashboard.import_preview.confirm_btn', 'Import')}
            </Button>
            <Button
              variant="ghost"
              className="w-full h-12 text-sm font-bold text-slate-400"
              onClick={onClose}
              data-testid="import-preview-cancel-btn"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
          </div>
        </div>
      )}
    </ResponsiveModal>
  );
};
